import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    getActivities,
    createActivity,
    updateActivity,
    deleteActivity,
    type ActivityData,
} from '../lib/api';
import { queryKeys } from '../lib/queryClient';

export function useActivities(token: string | null, schoolProfileId?: string) {
    return useQuery({
        queryKey: queryKeys.activities.list(schoolProfileId),
        queryFn: () => getActivities(token!, schoolProfileId),
        enabled: !!token && !!schoolProfileId,
        staleTime: 1000 * 60 * 5,
    });
}

export function useCreateActivity(token: string | null, schoolProfileId?: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: Omit<ActivityData, 'id'>) => createActivity(data, token!),

        onMutate: async (newActivity) => {
            await queryClient.cancelQueries({ queryKey: queryKeys.activities.list(schoolProfileId) });
            const previousActivities = queryClient.getQueryData<ActivityData[]>(queryKeys.activities.list(schoolProfileId));

            const tempId = `temp-${Date.now()}`;
            queryClient.setQueryData<ActivityData[]>(
                queryKeys.activities.list(schoolProfileId),
                (old = []) => [...old, { ...newActivity, id: tempId } as ActivityData]
            );

            return { previousActivities, tempId };
        },

        onSuccess: (createdActivity, _variables, context) => {
            queryClient.setQueryData<ActivityData[]>(
                queryKeys.activities.list(schoolProfileId),
                (old = []) => old.map(a => a.id === context?.tempId ? createdActivity : a)
            );
        },

        onError: (_err, _variables, context) => {
            if (context?.previousActivities) {
                queryClient.setQueryData(queryKeys.activities.list(schoolProfileId), context.previousActivities);
            }
        },
    });
}

export function useUpdateActivity(token: string | null, schoolProfileId?: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: string; data: Partial<ActivityData> }) =>
            updateActivity(id, data, token!),

        onMutate: async ({ id, data }) => {
            await queryClient.cancelQueries({ queryKey: queryKeys.activities.list(schoolProfileId) });
            const previousActivities = queryClient.getQueryData<ActivityData[]>(queryKeys.activities.list(schoolProfileId));

            queryClient.setQueryData<ActivityData[]>(
                queryKeys.activities.list(schoolProfileId),
                (old = []) => old.map(a => a.id === id ? { ...a, ...data } : a)
            );

            return { previousActivities };
        },

        onSuccess: (updatedActivity, { id }) => {
            queryClient.setQueryData<ActivityData[]>(
                queryKeys.activities.list(schoolProfileId),
                (old = []) => old.map(a => a.id === id ? updatedActivity : a)
            );
        },

        onError: (_err, _variables, context) => {
            if (context?.previousActivities) {
                queryClient.setQueryData(queryKeys.activities.list(schoolProfileId), context.previousActivities);
            }
        },
    });
}

export function useDeleteActivity(token: string | null, schoolProfileId?: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => deleteActivity(id, token!),

        onMutate: async (deletedId) => {
            await queryClient.cancelQueries({ queryKey: queryKeys.activities.list(schoolProfileId) });
            const previousActivities = queryClient.getQueryData<ActivityData[]>(queryKeys.activities.list(schoolProfileId));

            queryClient.setQueryData<ActivityData[]>(
                queryKeys.activities.list(schoolProfileId),
                (old = []) => old.filter(a => a.id !== deletedId)
            );

            return { previousActivities };
        },

        onError: (_err, _deletedId, context) => {
            if (context?.previousActivities) {
                queryClient.setQueryData(queryKeys.activities.list(schoolProfileId), context.previousActivities);
            }
        },
    });
}
